import type { CardListItem } from "@hobbyshopify/backend-client";
import { formatCents } from "../core/primitives/card-display";
import { cardYearBrandNumber } from "../core/primitives/card-edit";
import {
  listedOnline,
  outcomeLine,
  pricedDate,
  type RepriceOutcome,
  rowMenu,
  stockTitle,
} from "../pages/cards/cards-model";
import { ActionsMenu, type MenuAction } from "./ActionsMenu";
import { ArchiveIcon, BoxIcon, ChartIcon, MinusIcon, PhotoIcon, PlusIcon, RefreshIcon, TagIcon } from "./review-icons";

const OUTCOME_TONE = { up: "text-accent", down: "text-danger", muted: "text-muted", danger: "text-danger" } as const;

/** The row's "…" menu: Set price and Count stock open the card at Price and stock, History at Price history, as on iOS. */
export function rowActions(
  canEdit: boolean,
  tracked: boolean,
  onOpen: (section: "price" | "history") => void,
  onArchive: () => void,
): MenuAction[] {
  const menu: Record<ReturnType<typeof rowMenu>[number], MenuAction> = {
    price: { key: "price", label: "Set price", icon: <TagIcon />, onSelect: () => onOpen("price") },
    count: { key: "count", label: "Count stock", icon: <BoxIcon />, onSelect: () => onOpen("price") },
    history: { key: "history", label: "History", icon: <ChartIcon />, onSelect: () => onOpen("history") },
    archive: {
      key: "archive",
      label: "Archive",
      icon: <ArchiveIcon />,
      danger: true,
      divided: true,
      onSelect: onArchive,
    },
  };
  return rowMenu(canEdit, tracked).map((key) => menu[key]);
}

/**
 * A card the shop sells, as iOS's Stock row (`AdminCardRow`): the photo and the
 * text open the card; the price can be looked up again and the count stepped
 * from the row; the rest is on the card page, through the "…" menu.
 */
export function CardRow({
  item,
  available,
  canEdit,
  busy,
  repricing,
  opening = false,
  openHeld = false,
  outcome,
  onOpen,
  onStep,
  onReprice,
  onArchive,
}: {
  item: CardListItem;
  /** The count to show: a tapped one not yet read back, else what the shop can sell. */
  available: number;
  /** Re-price, the count and archiving; History is for every role. */
  canEdit: boolean;
  /** Archiving, a price being put back, read again before it counts, or the page opening a card. */
  busy: boolean;
  repricing: boolean;
  /** Opening once its count lands. */
  opening?: boolean;
  /** Another card is opening: the photo and the title wait. */
  openHeld?: boolean;
  /** What the last lookup did, until the next one or Undo. */
  outcome: RepriceOutcome | null;
  onOpen: (section?: "price" | "history") => void;
  onStep: (step: number) => void;
  onReprice: () => void;
  onArchive: () => void;
}) {
  const { card } = item;
  // A sealed box arrives with a card-shaped stand-in; its own words are on `item`.
  const sealed = item.item?.kind === "sealed" ? item.item : null;
  const title = sealed?.title ?? stockTitle(card);
  const meta = sealed ? (sealed.subtitle ?? "") : cardYearBrandNumber(card);
  const listed = listedOnline(item);
  const line = outcome && !repricing ? outcomeLine(outcome) : null;
  const actions = rowActions(canEdit, card.trackStock, onOpen, onArchive);

  return (
    <div
      className={`flex gap-3 rounded-row border border-line bg-surface p-3 transition-opacity ${busy ? "opacity-60" : ""}`}
    >
      <button
        type="button"
        onClick={() => {
          if (!openHeld) onOpen();
        }}
        aria-label={`Open ${title}`}
        aria-disabled={openHeld || undefined}
        data-open-card={card.id}
        className="h-[90px] w-16 shrink-0 overflow-hidden rounded-btn bg-surface-strong aria-disabled:cursor-default"
      >
        {card.imageUrl ? (
          <img src={card.imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-faint">
            <PhotoIcon />
          </span>
        )}
      </button>

      {/* A container: on the narrowest screens the stepper goes under the price, which then has the line. */}
      <div className="@container flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start gap-1">
          <button
            type="button"
            onClick={() => {
              if (!openHeld) onOpen();
            }}
            aria-disabled={openHeld || undefined}
            className="min-w-0 flex-1 space-y-0.5 text-left aria-disabled:cursor-default"
          >
            <span className="block truncate text-[15px] font-semibold text-white">{title}</span>
            {meta && <span className="block truncate text-xs text-muted">{meta}</span>}
            {card.lastPricedAt && (
              <span className="block text-[11px] text-faint">Priced {pricedDate(card.lastPricedAt)}</span>
            )}
            {listed != null && (
              <span className="block text-[11px] text-warn">Listed at {formatCents(listed)} online</span>
            )}
          </button>
          <ActionsMenu label="Card actions" actions={actions} disabled={busy} />
        </div>
        {opening && (
          <p role="status" className="text-[11px] text-muted">
            Opening…
          </p>
        )}

        <div className="flex flex-col gap-1 @min-[13rem]:flex-row @min-[13rem]:items-center @min-[13rem]:gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-0.5">
              {card.currentPriceCents != null ? (
                <span className="min-w-0 truncate text-[17px] font-semibold tabular-nums text-accent">
                  {formatCents(card.currentPriceCents)}
                </span>
              ) : (
                // Not an error: the lookup found nothing and nobody has typed a number. Catalogued, not for sale.
                <span className="min-w-0 truncate text-[13px] font-semibold text-warn">Not priced</span>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    if (!busy && !repricing) onReprice();
                  }}
                  // Held, not disabled, while it looks up: the focus stays on it.
                  aria-disabled={busy || repricing || undefined}
                  aria-label={repricing ? "Checking eBay" : "Refresh price"}
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-btn ${
                    repricing ? "text-muted" : "text-accent hover:bg-surface"
                  } aria-disabled:cursor-default`}
                >
                  <span className={repricing ? "animate-spin motion-reduce:animate-none" : ""}>
                    <RefreshIcon />
                  </span>
                </button>
              )}
            </div>
            {line && (
              <p className={`line-clamp-2 text-xs font-semibold tabular-nums ${OUTCOME_TONE[line.tone]}`}>
                {line.text}
              </p>
            )}
          </div>

          {canEdit && (
            <div className="self-end @min-[13rem]:self-auto">
              {card.trackStock ? (
                <Stepper available={available} disabled={busy} onStep={onStep} />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (!busy) onOpen("price");
                  }}
                  // Held, not disabled: a press while the row waits keeps the focus here.
                  aria-disabled={busy || undefined}
                  className="h-8 shrink-0 rounded-btn border border-line px-2.5 text-[13px] font-semibold whitespace-nowrap text-muted hover:text-white aria-disabled:cursor-default aria-disabled:hover:text-muted"
                >
                  Track stock
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The count is the stock badge too: amber at zero, which is out of stock. Its
 * buttons are held rather than disabled, so the focus stays on "−" at zero.
 */
function Stepper({
  available,
  disabled,
  onStep,
}: {
  available: number;
  disabled: boolean;
  onStep: (step: number) => void;
}) {
  const out = available === 0;
  return (
    <fieldset
      aria-label="Stock"
      className={`m-0 flex min-w-0 shrink-0 items-center rounded-btn border bg-surface p-0 text-accent ${
        out ? "border-warn/55" : "border-line"
      }`}
    >
      <button
        type="button"
        onClick={() => {
          if (!disabled && !out) onStep(-1);
        }}
        aria-disabled={disabled || out || undefined}
        aria-label="Remove one"
        className="flex h-8 w-[30px] items-center justify-center aria-disabled:cursor-default aria-disabled:text-faint"
      >
        <MinusIcon />
      </button>
      <span
        aria-live="polite"
        className={`min-w-6 text-center text-sm font-semibold tabular-nums ${out ? "text-warn" : "text-white"}`}
      >
        <span aria-hidden="true">{available}</span>
        <span className="sr-only">{out ? "Out of stock" : `${available} in stock`}</span>
      </span>
      <button
        type="button"
        onClick={() => {
          if (!disabled) onStep(1);
        }}
        aria-disabled={disabled || undefined}
        aria-label="Add one"
        className="flex h-8 w-[30px] items-center justify-center aria-disabled:cursor-default aria-disabled:text-faint"
      >
        <PlusIcon />
      </button>
    </fieldset>
  );
}
